import { catchError, concatMap, of } from 'rxjs';
import { CognitoAuthService } from '../auth/services/cognito-auth.service';
import { NavigationKey } from '../generated/graphql';
import { NavigationService } from '../shared/services/navigation.service';
import { AppInjector } from '../app-injector.service';
import { Router } from '@angular/router';
import { defaultShellKey } from './pipeline/pipeline-shells';

export const loadSalesPipelineModule = () =>
  import('./pipeline/pipeline.module').then((m) => m.PipelineModule);

export const loadTicketingPipelineModule = () =>
  import('./pipeline-ticketing/pipeline-ticketing.module').then((m) => m.PipelineTicketingModule);

/**
 * EC-458: which implementation answers bare `/pipeline` is a navigation key like any other
 * section's; reachability is still the PIPELINE key. EC-758: the backend sends every shell the
 * user holds, and bare `/pipeline` composes the default one (the first held). A user with two
 * boards is redirected by `PipelineAuthGuard` to the board's own route before this loads.
 */
const pipelineModules: [NavigationKey, () => Promise<unknown>][] = [
  [NavigationKey.PipelineSales, loadSalesPipelineModule],
  [NavigationKey.PipelineTicketing, loadTicketingPipelineModule],
  [
    NavigationKey.PipelineSearch,
    () => import('./flight-requests/flight-requests.module').then((m) => m.FlightRequestsModule),
  ],
];

export const getPipelineModule = () => () => {
  const router = AppInjector.injector.get(Router);
  const cognitoAuthService = AppInjector.injector.get(CognitoAuthService);
  const navigationService = AppInjector.injector.get(NavigationService);

  return new Promise((resolve) =>
    cognitoAuthService
      .hasCognitoSession()
      .pipe(
        concatMap(() => navigationService.navigation$),
        catchError(() => {
          void router.navigate(['login'], {
            queryParams: {
              returnUrl: window.location,
            },
          });
          return of(null);
        }),
      )
      .subscribe((keys) => {
        if (!keys) {
          return;
        }

        const shellKey = defaultShellKey(keys);
        const [, loadModule] =
          pipelineModules.find(([key]) => key === shellKey) ?? pipelineModules[0];

        void loadModule().then((m) => resolve(m));
      }),
  );
};
